/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * CartService - Centralized service for all shopping cart GraphQL operations
 *
 * @module CartService
 * @description Provides a unified interface for cart operations including create, update, retrieve, and delete.
 * Used by both RLM 2.0 (checkout redirect flow) and Mixed Inventory (multi-room on-page flow).
 *
 * @usage
 * const cartService = useCartService();
 * const { createCart, addItemToCart, states } = cartService;
 *
 * @operations
 * - createCart: Creates a new shopping cart (first room selection)
 * - addItemToCart: Adds additional items/rooms to existing cart (subsequent selections)
 * - removeItemFromCart: Removes items from cart
 * - getCartById: Retrieves cart details by ID
 * - deleteCartById: Permanently deletes a cart
 * - extendCartExpiration: Extends cart expiration window
 *
 * @states
 * Each operation returns loading, error, and data states for UI feedback
 */
import { gql, useMutationFetch, useQueryFetch } from '../../utils';
import { GQL_OPT_NAME_DTT } from '../../constants';
import gqlCreateCart from '../cart/mutations/createCart.graphql';
import gqlAddItemToCart from '../cart/mutations/addItemToCart.graphql';
import gqlRemoveItemFromCart from '../cart/mutations/removeItemFromCart.graphql';
import gqlDeleteCartById from '../cart/mutations/deleteCartById.graphql';
import gqlExtendCartExpiration from '../cart/mutations/extendCartExpiration.graphql';
import gqlGetCartById from '../cart/queries/getCartById.graphql';
import { GraphQLOperationFn } from './CartService.types';

/**
 * GraphQL mutation to create a new shopping cart
 * @imported from createCart.graphql file
 * @returns Cart ID and full cart object
 */
const CREATE_CART = gql`
  ${gqlCreateCart}
`;

/**
 * GraphQL mutation to add items/rooms to an existing cart
 *
 * @param {CommerceAddItemToCartInput[]} input - Contains cartId and itemGroups to add
 * @returns Updated cart with new items
 *
 * @useCase Mixed Inventory: Adding 2nd, 3rd, 4th... rooms to existing cart
 * @useCase Modify flow: Adding additional items during modification
 */
const ADD_ITEM_TO_CART = gql`
  ${gqlAddItemToCart}
`;

/**
 * GraphQL mutation to remove items from an existing cart
 *
 * @param {CommerceRemoveItemFromCartInput} removeItemFromCartInput - Contains cartId and item IDs to remove
 * @returns Updated cart without removed items
 *
 * @useCase User removes a selected room before proceeding to checkout
 * @useCase Cleaning up cart when user changes search criteria
 */
const REMOVE_ITEM_FROM_CART = gql`
  ${gqlRemoveItemFromCart}
`;

/**
 * GraphQL query to retrieve cart details by ID
 *
 * @param {ID} cartId - Unique cart identifier
 * @returns Cart object with id, itemGroups, and error/warning information
 *
 * @useCase Mixed Inventory: Validating cart state before adding more rooms
 * @useCase Persistent Cart: Retrieving saved cart on return visit
 * @useCase Error recovery: Checking cart status after failed operations
 *
 * @contentError Applies custom error handling with WEB-BOOK-GEN001 fallback code
 */
const GET_CART_BY_ID = gql`
  ${gqlGetCartById}
`;

/**
 * GraphQL mutation to permanently delete a cart
 *
 * @param {ID} cartId - Unique cart identifier to delete
 * @returns Deleted cart ID and status information
 *
 * @useCase User abandons cart and starts fresh search
 * @useCase Session cleanup when cart expires
 * @useCase Error recovery when cart is in invalid state
 */
const DELETE_CART_BY_ID = gql`
  ${gqlDeleteCartById}
`;

/**
 * GraphQL mutation to extend cart expiration
 *
 * @param {CommerceExtendCartExpirationInput} input - Contains cartId and itemGroups to refresh
 * @returns Updated cart ID and status information
 */
const EXTEND_CART_EXPIRATION = gql`
  ${gqlExtendCartExpiration}
`;

/**
 * Custom hook that provides cart service operations and their states
 *
 * @returns {Object} Cart service object
 * @returns {Function} createCart - Creates new cart (async)
 * @returns {Function} addItemToCart - Adds items to existing cart (async)
 * @returns {Function} removeItemFromCart - Removes items from cart (async)
 * @returns {Function} getCartById - Retrieves cart by ID (async)
 * @returns {Function} deleteCartById - Deletes cart by ID (async)
 * @returns {Function} extendCartExpiration - Extends cart expiration (async)
 * @returns {Object} states - Loading/error/data states for all operations
 *
 */
export const useCartService = () => {
  const {
    createCart: createCartOptName,
    addItemToCart: addItemToCartOptName,
    removeItemFromCart: removeItemFromCartOptName,
    getCartById: getCartByIdOptName,
    deleteCartById: deleteCartByIdOptName,
    extendCartExpiration: extendCartExpirationOptName,
  } = GQL_OPT_NAME_DTT;
  // Initialize createCart mutation with state management
  const [createCartMutation, createCartState] = useMutationFetch(CREATE_CART, createCartOptName);

  // Initialize addItemToCart mutation with state management
  const [addItemToCartMutation, addItemToCartState] = useMutationFetch(ADD_ITEM_TO_CART, addItemToCartOptName);

  // Initialize removeItemFromCart mutation with state management
  const [removeItemFromCartMutation, removeItemFromCartState] = useMutationFetch(
    REMOVE_ITEM_FROM_CART,
    removeItemFromCartOptName
  );

  // Initialize getCartById query with state management
  const [getCartByIdQuery, getCartByIdState] = useQueryFetch(GET_CART_BY_ID, getCartByIdOptName);

  // Initialize deleteCartById mutation with state management
  const [deleteCartByIdMutation, deleteCartByIdState] = useMutationFetch(DELETE_CART_BY_ID, deleteCartByIdOptName);

  // Initialize extendCartExpiration mutation with state management
  const [extendCartExpirationMutation, extendCartExpirationState] = useMutationFetch(
    EXTEND_CART_EXPIRATION,
    extendCartExpirationOptName
  );

  /**
   * Creates a new shopping cart
   * @param {Object} options - GraphQL options with variables
   * @param {Object} options.variables - Cart creation input payload
   * @returns {Promise} GraphQL response with cart data or errors
   */
  const createCart: GraphQLOperationFn = async options => createCartMutation(options);

  /**
   * Adds items/rooms to an existing shopping cart
   * @param {Object} options - GraphQL options with variables
   * @param {Object} options.variables.input - Contains cartId and itemGroups
   * @returns {Promise} GraphQL response with updated cart or errors
   */
  const addItemToCart: GraphQLOperationFn = async options => addItemToCartMutation(options);

  /**
   * Removes items from an existing shopping cart
   * @param {Object} options - GraphQL options with variables
   * @param {Object} options.variables.removeItemFromCartInput - Contains cartId and item IDs to remove
   * @returns {Promise} GraphQL response with updated cart or errors
   */
  const removeItemFromCart: GraphQLOperationFn = async options => removeItemFromCartMutation(options);

  /**
   * Retrieves cart details by cart ID
   * @param {Object} options - GraphQL options with variables
   * @param {String} options.variables.cartId - Unique cart identifier
   * @returns {Promise} GraphQL response with cart details or errors
   */
  const getCartById: GraphQLOperationFn = async options => getCartByIdQuery(options);

  /**
   * Permanently deletes a cart by ID
   * @param {Object} options - GraphQL options with variables
   * @param {String} options.variables.cartId - Unique cart identifier to delete
   * @returns {Promise} GraphQL response with deletion confirmation or errors
   */
  const deleteCartById: GraphQLOperationFn = async options => deleteCartByIdMutation(options);

  /**
   * Extends cart expiration
   * @param {Object} options - GraphQL options with variables
   * @param {Object} options.variables.input - Contains cartId and itemGroups
   * @returns {Promise} GraphQL response with updated cart or errors
   */
  const extendCartExpiration: GraphQLOperationFn = async options => extendCartExpirationMutation(options);

  return {
    // Cart operations (async functions)
    createCart,
    addItemToCart,
    removeItemFromCart,
    getCartById,
    deleteCartById,
    extendCartExpiration,

    // Operation states for UI feedback
    // Each state contains: { loading: boolean, error: Error | null, data: any }
    states: {
      createCartState, // State for cart creation operation
      addItemToCartState, // State for adding items operation
      removeItemFromCartState, // State for removing items operation
      getCartByIdState, // State for cart retrieval operation
      deleteCartByIdState, // State for cart deletion operation
      extendCartExpirationState, // State for cart expiration extension operation
    },
  };
};

/**
 * Alias export for backward compatibility
 * @deprecated Use useCartService directly for clarity
 */
export const CartService = useCartService;
